import { useEffect, useState } from "react";
import { updatePayout, useAction } from "wasp/client/operations";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import PayoutAmountFields from "./PayoutAmountFields";
import { EMPTY_PAYOUT_VALUES, payoutFormValuesFrom, resolvePayoutForm, type PayoutFormValues } from "./payoutForm";

export interface EditablePayout {
  id: string;
  date: Date | string;
  amount: number;
  grossAmount?: number | null;
  notes?: string | null;
}

interface Props {
  payout: EditablePayout | null;
  onClose: () => void;
  onSaved?: () => void;
}

const toDateInput = (d: Date | string) => new Date(d).toISOString().split("T")[0];

/** Modification d'un payout existant : date, brut / net, notes. */
export default function EditPayoutDialog({ payout, onClose, onSaved }: Props) {
  const t = useTranslation();
  const { toast } = useToast();
  const updatePayoutFn = useAction(updatePayout);

  const [values, setValues] = useState<PayoutFormValues>(EMPTY_PAYOUT_VALUES);
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!payout) return;
    setValues(payoutFormValuesFrom(payout));
    setDate(toDateInput(payout.date));
    setNotes(payout.notes ?? "");
  }, [payout]);

  if (!payout) return null;

  const handleSave = async () => {
    const resolved = resolvePayoutForm(values);
    if (!resolved.ok) {
      toast({
        title: resolved.reason === "gross_below_net" ? t.tradingAccounts.payoutGrossBelowNet : t.tradingAccounts.payoutAmountsRequired,
        variant: "destructive",
      });
      return;
    }
    if (!date) return;
    setSaving(true);
    try {
      await updatePayoutFn({
        id: payout.id,
        date,
        amount: resolved.amount,
        grossAmount: resolved.grossAmount,
        notes: notes.trim() || null,
      });
      toast({ title: t.tradingAccounts.payoutUpdated });
      onSaved?.();
      onClose();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.tradingAccounts.payoutEdit}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <PayoutAmountFields values={values} onChange={setValues} idPrefix={`edit-payout-${payout.id}`} />
          <div className="space-y-1">
            <Label htmlFor="edit-payout-date" className="text-xs text-muted-foreground">{t.tradingAccounts.payoutDate}</Label>
            <Input id="edit-payout-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit-payout-notes" className="text-xs text-muted-foreground">{t.tradingAccounts.payoutNotes}</Label>
            <Input id="edit-payout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="h-9" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            {t.tradingAccounts.cancel}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {t.tradingAccounts.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
